/**
 * GateModal — Unified freemium gate modal
 * Three views: Signup (guest) | Limit Reached (free user) | Pass Required (needs upgrade)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LockIcon from '@mui/icons-material/Lock';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import { useLanguage } from '../../utils/LanguageContext';
import { GATE_REASONS, GATE_TEXTS } from '../../constants/gateConfig';

const GateModal = ({ open, onClose, gateState }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const txt = GATE_TEXTS[language] || GATE_TEXTS.en;

  if (!gateState) return null;

  const { reason, used, limit, minimumPass } = gateState;
  const isSignup = reason === GATE_REASONS.SIGNUP_REQUIRED;
  const isLimit = reason === GATE_REASONS.LIMIT_REACHED;
  const isPassRequired = reason === GATE_REASONS.PASS_REQUIRED;

  // Can dismiss unless it's a hard block (limit fully exhausted)
  const canDismiss = true;

  const handleSignUp = () => {
    onClose();
    navigate('/auth?mode=signup');
  };

  const handleSignIn = () => {
    onClose();
    navigate('/auth');
  };

  const handleUpgrade = () => {
    onClose();
    navigate('/app', { state: { view: 'account' } });
  };

  // Title & subtitle
  const getTitle = () => {
    if (isSignup) return txt.signupTitle;
    if (isLimit) return txt.limitTitle;
    if (isPassRequired) return txt.passTitle;
    return txt.signupTitle;
  };

  const getSubtitle = () => {
    if (isSignup) {
      return (used > 0) ? txt.signupSubtitleLimit : txt.signupSubtitle;
    }
    if (isLimit) {
      return txt.limitSubtitle
        .replace('{used}', used || 0)
        .replace('{limit}', limit || 0);
    }
    if (isPassRequired) {
      const passName = txt.passNames?.[minimumPass] || minimumPass || 'Access Pass';
      return txt.passSubtitle.replace('{pass}', passName);
    }
    return txt.signupSubtitle;
  };

  // Header icon
  const HeaderIcon = isPassRequired ? LockIcon : isLimit ? TimerOffIcon : RocketLaunchIcon;

  return (
    <Dialog
      open={open}
      onClose={canDismiss ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Header gradient */}
      <Box
        sx={{
          background: isPassRequired
            ? 'linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)'
            : isLimit
              ? 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)'
              : 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
          py: 3,
          px: 3,
          position: 'relative',
        }}
      >
        {canDismiss && (
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <HeaderIcon sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color="white">
              {getTitle()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              {getSubtitle()}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Benefits list */}
        <List disablePadding>
          {txt.benefits.map((benefit, i) => (
            <ListItem key={i} disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={benefit} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', gap: 1 }}>
        {isSignup ? (
          <>
            <Button fullWidth variant="contained" size="large" onClick={handleSignUp} sx={{ py: 1.5 }}>
              {txt.signUp}
            </Button>
            <Button fullWidth variant="text" onClick={handleSignIn} sx={{ color: 'text.secondary' }}>
              {txt.signIn}
            </Button>
          </>
        ) : (
          <Button fullWidth variant="contained" size="large" onClick={handleUpgrade} sx={{ py: 1.5 }}>
            {txt.upgrade}
          </Button>
        )}

        {canDismiss && (
          <Button fullWidth variant="text" onClick={onClose} sx={{ color: 'text.disabled', fontSize: '0.875rem' }}>
            {txt.maybeLater}
          </Button>
        )}

        <Typography variant="caption" color="text.disabled" sx={{ mt: 2, textAlign: 'center' }}>
          {txt.poweredBy}
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default GateModal;
